"use client"

import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import styled from "styled-components"
import PostCard from "./PostCard"
import { ArrowLeft, Loader, AlertCircle } from "lucide-react"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #374151;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
  width: fit-content;
  
  &:hover {
    background-color: #f9fafb;
    border-color: #d1d5db;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  gap: 16px;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  gap: 16px;
`

const ErrorIcon = styled.div`
  color: #ef4444;
`

const ErrorTitle = styled.h2`
  font-size: 20px;
  color: #111827;
  margin: 0;
`

const ErrorMessage = styled.p`
  color: #6b7280;
  margin: 0;
  text-align: center;
`

const RelatedPostsSection = styled.div`
  margin-top: 40px;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 16px;
  color: #111827;
`

const RelatedPosts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export default function PostDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [post, setPost] = useState(null)
  const [relatedPosts, setRelatedPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchPostDetail()
  }, [id])

  const fetchPostDetail = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch(`/post/${id}`, {
        credentials: 'include'
      })
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('게시물을 찾을 수 없습니다.')
        } else {
          setError('게시물을 불러오는 중 오류가 발생했습니다.')
        }
        return
      }
      
      const data = await response.json()
      
      if (data.success) {
        console.log("게시물 상세 정보:", data.post)
        setPost(data.post)
        
        // 관련 게시물 가져오기 (같은 태그를 가진 게시물)
        if (data.post.tags && data.post.tags.length > 0) {
          fetchRelatedPosts(data.post.tags[0])
        }
      } else {
        setError(data.message || '게시물을 불러올 수 없습니다.')
      }
    } catch (error) {
      console.error("게시물 로드 오류:", error)
      setError('서버 연결에 문제가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  const fetchRelatedPosts = async (tag) => {
    try {
      const response = await fetch(`/post/by-tag/${tag}?limit=3`, {
        credentials: 'include'
      })
      
      const data = await response.json()
      
      if (data.success) {
        // 현재 게시물 제외
        const filtered = data.posts.filter(p => p.id !== parseInt(id))
        setRelatedPosts(filtered.slice(0, 2))
      }
    } catch (error) {
      console.error("관련 게시물 로드 오류:", error)
    }
  }

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Loader size={32} className="animate-spin" />
          <div>게시물을 불러오는 중...</div>
        </LoadingContainer>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <BackButton onClick={handleBack}>
          <ArrowLeft size={16} />
          돌아가기
        </BackButton>
        <ErrorContainer>
          <ErrorIcon>
            <AlertCircle size={48} />
          </ErrorIcon>
          <ErrorTitle>게시물을 찾을 수 없습니다</ErrorTitle>
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorContainer>
      </Container>
    )
  }

  if (!post) {
    return null
  }

  // 이미지 처리
  let processedImages = []
  if (post.images) {
    let images = post.images
    if (typeof images === 'string') {
      try {
        images = JSON.parse(images)
      } catch (e) {
        images = []
      }
    }
    if (Array.isArray(images)) {
      processedImages = images.map(img => 
        typeof img === 'object' ? (img.url || img.image_url) : img
      ).filter(url => url)
    }
  }

  return (
    <Container>
      <BackButton onClick={handleBack}>
        <ArrowLeft size={16} />
        돌아가기
      </BackButton>
      
      <PostCard 
        post={{
          id: post.id,
          author: post.author_username,
          authorAvatar: post.author_avatar,
          timestamp: post.created_at,
          images: processedImages,
          tags: post.tags || [],
          description: post.content,
          like_count: post.like_count,
          comment_count: post.comment_count
        }}
        onPostDeleted={() => navigate('/')}
        onPostUpdated={() => fetchPostDetail()}
      />
      
      {relatedPosts.length > 0 && (
        <RelatedPostsSection>
          <SectionTitle>관련 게시물</SectionTitle>
          <RelatedPosts>
            {relatedPosts.map(relatedPost => {
              // 관련 게시물 이미지 처리
              let relatedImages = []
              if (relatedPost.images) {
                let images = relatedPost.images
                if (typeof images === 'string') {
                  try {
                    images = JSON.parse(images)
                  } catch (e) {
                    images = []
                  }
                }
                if (Array.isArray(images)) {
                  relatedImages = images.map(img => 
                    typeof img === 'object' ? (img.url || img.image_url) : img
                  ).filter(url => url)
                }
              }
              
              return (
                <PostCard 
                  key={relatedPost.id}
                  post={{
                    id: relatedPost.id,
                    author: relatedPost.author_username,
                    authorAvatar: relatedPost.author_avatar,
                    timestamp: relatedPost.created_at,
                    images: relatedImages,
                    tags: relatedPost.tags || [],
                    description: relatedPost.content,
                    like_count: relatedPost.like_count,
                    comment_count: relatedPost.comment_count
                  }}
                  onPostDeleted={() => fetchRelatedPosts()}
                  onPostUpdated={() => fetchRelatedPosts()}
                />
              )
            })}
          </RelatedPosts>
        </RelatedPostsSection>
      )}
    </Container>
  )
}